import { useState, useCallback, useRef } from 'react';
import { useAuthStore } from '../../lib/auth';
import { useTranslation } from '../../lib/i18n';
import api from '../../lib/api';
import { toast } from 'sonner';

// Components
import { RoutesHeader } from './components/RoutesHeader';
import { RoutesFilters } from './components/RoutesFilters';
import { RoutesSummary } from './components/RoutesSummary';
import { RoutesTable } from './components/RoutesTable';
import { RouteFormDialog } from './components/RouteFormDialog';
import { ImportDialog } from './components/ImportDialog';
import RouteAttachments from './components/RouteAttachments';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// Hooks & Utils
import { useRoutes } from './hooks/useRoutes';
import { usePreviousPeriodSummary } from './hooks/usePreviousPeriodSummary';
import { calculateSummary } from './utils/calculations';
import { buildRoutesSheet } from './utils/exportRoutes';

export default function Routes() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [importDialog, setImportDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [attachmentsRoute, setAttachmentsRoute] = useState(null);
  const formAttachmentsRef = useRef(null);

  // Use custom hook for all route operations
  const {
    routes,
    filteredRoutes,
    firms,
    employees,
    vehicles,
    loading,
    selectedCustomers,
    selectedFirm,
    selectedMonths,
    searchQuery,
    customerList,
    dateRange,
    setSelectedFirm,
    setSelectedMonths,
    setSearchQuery,
    setDateRange,
    toggleCustomer,
    isMultiCustomer,
    primaryCustomer,
    selectedRows,
    bulkDeleting,
    bulkCloning,
    recentlyClonedIds,
    toggleRowSelection,
    toggleAllRows,
    handleBulkDelete,
    handleBulkClone,
    formData,
    setFormData,
    editingRoute,
    saving,
    addDialog,
    setAddDialog,
    handleSaveRoute,
    handleDeleteRoute,
    handleCloneRoute,
    handleFormCustomerChange,
    openAddDialog,
    openEditDialog,
    updateRouteInline,
    updateRouteDriver,
    fetchRoutes,
  } = useRoutes(user);

  // Calculate summary
  const summary = calculateSummary(filteredRoutes);
  // KM kartı: müşteri ID'sine değil veriye göre — herhangi bir satırda km_total
  // pozitifse göster. Böylece sonradan eklenen müşteriler için de otomatik
  // olarak görünür (önceden sadece fedex/bpost için hardcoded'du).
  const showKm = filteredRoutes.some((r) => Number(r.km_total) > 0);

  // Önceki dönem özeti (trend okları için) — tek müşteri + tarih/ay filtresi
  // varsa hesaplanır.
  const { previousSummary } = usePreviousPeriodSummary({
    selectedCustomers, selectedFirm, selectedMonths, dateRange,
  });

  // Fetch employees for inline editing
  const fetchEmployeesForInline = useCallback(async (firmId) => {
    try {
      const params = new URLSearchParams();
      if (firmId) params.append('firm_id', firmId);
      
      const response = await api.get(`/employee-profile/list?${params.toString()}`);
      if (response.data.success) {
        const allEmployees = response.data.data?.employees || [];
        return allEmployees.filter(emp => emp.user?.role !== 'SUPER_ADMIN');
      }
      return [];
    } catch (error) {
      console.error('Error fetching employees:', error);
      return [];
    }
  }, []);

  // Fetch ALL employees (across all firms) for "Tüm Şoförler" feature
  // Şub 10, 2026 — limit=5000 — eski default 50 idi, çoklu firma + büyük çalışan
  // listelerinde diğer firma çalışanları gözükmüyordu.
  const fetchAllEmployees = useCallback(async () => {
    try {
      const response = await api.get('/employee-profile/list?limit=5000&user_status=ACTIVE');
      if (response.data.success) {
        const allEmp = response.data.data?.employees || [];
        return allEmp.filter(emp => emp.user?.role !== 'SUPER_ADMIN');
      }
      return [];
    } catch (error) {
      console.error('Error fetching all employees:', error);
      return [];
    }
  }, []);

  // Bulk Recalculate
  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      const body = {};
      if (primaryCustomer) body.customer = primaryCustomer;
      if (selectedFirm) body.firm_id = selectedFirm;
      
      const response = await api.post('/routes/recalculate', body);
      if (response.data.success) {
        const { updated, skipped, errors } = response.data.data;
        toast.success(`${updated} ${t('routes.messages.recalculated') || 'kayıt yeniden hesaplandı'}${skipped > 0 ? `, ${skipped} ${t('routes.messages.alreadyCorrect') || 'zaten doğru'}` : ''}`);
        fetchRoutes();
      }
    } catch (error) {
      toast.error(t('routes.messages.recalculateError') || 'Yeniden hesaplama hatası');
    } finally {
      setRecalculating(false);
    }
  };

  // Excel Export — seçili her müşteri ayrı sayfa, müşterinin tüm kolonları; ekrandaki arama filtresi uygulanır.
  const handleExport = async () => {
    try {
      setExporting(true);
      const customers = Array.from(selectedCustomers).length ? Array.from(selectedCustomers) : [primaryCustomer];
      const visibleIds = new Set(filteredRoutes.map((r) => r.id));
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      let total = 0;
      let lastName = 'routes.xlsx';
      for (const customer of customers) {
        const params = new URLSearchParams();
        params.append('customer', customer);
        if (selectedFirm) params.append('firm_id', selectedFirm);
        if (dateRange.start && dateRange.end) {
          params.append('start_date', dateRange.start);
          params.append('end_date', dateRange.end);
        } else if (selectedMonths.length > 0) {
          params.append('month', selectedMonths.join(','));
        }
        const response = await api.get(`/routes/export?${params.toString()}`);
        if (!response.data.success) continue;
        const { headers, rows, ids, filename, numeric_columns: numericCols } = response.data.data;
        const keptRows = Array.isArray(ids) ? rows.filter((_, i) => visibleIds.has(ids[i])) : rows;
        const sheetName = (customerList.find((c) => c.slug === customer)?.name || customer).slice(0, 31);
        XLSX.utils.book_append_sheet(wb, buildRoutesSheet(XLSX, headers, keptRows, numericCols), sheetName);
        total += keptRows.length;
        lastName = filename || lastName;
      }
      const outName = customers.length > 1 ? lastName.replace(/^routes_[^_]+_/, 'routes_coklu_') : lastName;
      XLSX.writeFile(wb, outName);
      toast.success(`${total} ${t('routes.messages.exportSuccess') || 'kayıt indirildi'}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('routes.messages.exportError') || 'Dışa aktarma hatası');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <RoutesHeader 
        onImport={() => setImportDialog(true)}
        onExport={handleExport}
        onAdd={openAddDialog}
        onRecalculate={handleRecalculate}
        exporting={exporting}
        recalculating={recalculating}
        t={t}
        userRole={user?.role}
      />

      <RoutesFilters
        selectedCustomers={selectedCustomers}
        selectedFirm={selectedFirm}
        selectedMonths={selectedMonths}
        searchQuery={searchQuery}
        firms={firms}
        customerList={customerList}
        dateRange={dateRange}
        onCustomerToggle={toggleCustomer}
        onFirmChange={setSelectedFirm}
        onMonthsChange={setSelectedMonths}
        onDateRangeChange={setDateRange}
        onSearchChange={setSearchQuery}
        t={t}
      />

      <RoutesSummary 
        summary={summary} 
        previousSummary={previousSummary}
        showKm={showKm}
        t={t}
      />

      <RoutesTable
        routes={filteredRoutes}
        loading={loading}
        selectedCustomers={selectedCustomers}
        isMultiCustomer={isMultiCustomer}
        selectedRows={selectedRows}
        bulkDeleting={bulkDeleting}
        bulkCloning={bulkCloning}
        recentlyClonedIds={recentlyClonedIds}
        firms={firms}
        customerList={customerList}
        onToggleRow={toggleRowSelection}
        onToggleAllRows={toggleAllRows}
        onBulkDelete={handleBulkDelete}
        onBulkClone={handleBulkClone}
        onEdit={openEditDialog}
        onDelete={handleDeleteRoute}
        onClone={handleCloneRoute}
        onAdd={openAddDialog}
        onUpdateInline={updateRouteInline}
        onUpdateDriver={updateRouteDriver}
        onFetchEmployees={fetchEmployeesForInline}
        onViewAttachments={(route) => setAttachmentsRoute(route)}
        t={t}
      />

      <RouteFormDialog
        open={addDialog}
        onOpenChange={setAddDialog}
        formData={formData}
        setFormData={setFormData}
        editingRoute={editingRoute}
        firms={firms}
        employees={employees}
        vehicles={vehicles}
        saving={saving}
        onSave={() => handleSaveRoute(formAttachmentsRef)}
        onCustomerChange={handleFormCustomerChange}
        onFetchAllEmployees={fetchAllEmployees}
        attachmentsRef={formAttachmentsRef}
        customerList={customerList}
        t={t}
      />

      {/* Attachments Dialog */}
      <Dialog open={!!attachmentsRoute} onOpenChange={() => setAttachmentsRoute(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>Dosya Ekleri</span>
              {attachmentsRoute && (
                <span className="text-sm font-normal text-slate-500">
                  {attachmentsRoute.date} - {attachmentsRoute.route_number || attachmentsRoute.rit_number || 'Route'}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {attachmentsRoute && (
            <RouteAttachments 
              routeId={attachmentsRoute.id} 
              isEditing={true}
            />
          )}
        </DialogContent>
      </Dialog>

      <ImportDialog
        open={importDialog}
        onOpenChange={setImportDialog}
        primaryCustomer={primaryCustomer}
        isMultiCustomer={isMultiCustomer}
        firms={firms}
        selectedFirm={selectedFirm}
        user={user}
        onImportComplete={fetchRoutes}
        t={t}
      />
    </div>
  );
}
